import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import authService from "../../../../services/authService";
import { customerService, type Customer } from "../../../../services/customerService";
import { resolvePreferredUsername } from "../../../../utils/authBootstrap";
import { getCurrentUserRoles, isAdmin } from "../../../../utils/authorization";

async function loadLinkedCustomer(): Promise<Customer | null> {
  const user = await authService.getUser();
  const username = resolvePreferredUsername(user);
  return customerService.getMyCustomer(username);
}

async function loadAllCustomers(): Promise<Customer[]> {
  const acc: Customer[] = [];
  let page = 0;
  const size = 50;

  for (;;) {
      const response = await customerService.getCustomers(page, size, undefined, [
        "id",
        "name",
        "timeZone",
        "locale",
      ]);
    const chunk = (response?.content ?? []) as Customer[];
    acc.push(...chunk.filter((c) => typeof c.id === "string" && c.id.length > 0));
    if (chunk.length < size) break;
    page += 1;
    if (page > 100) break;
  }

  return acc.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }));
}

export function useDashboardOverviewCustomers() {
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [rolesResolved, setRolesResolved] = useState(false);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => {
      setIsAdminUser(isAdmin(roles));
      setRolesResolved(true);
    });
  }, []);

  const customersQuery = useQuery({
    queryKey: ["dashboard-overview", "customers", isAdminUser],
    queryFn: async () => {
      if (isAdminUser) {
        return loadAllCustomers();
      }
      const linked = await loadLinkedCustomer();
      return linked?.id ? [linked] : [];
    },
    enabled: rolesResolved,
    staleTime: 60_000,
  });

  return {
    isAdminUser,
    customers: customersQuery.data ?? [],
    loading: !rolesResolved || customersQuery.isLoading,
    error: customersQuery.error instanceof Error ? customersQuery.error.message : null,
    refetch: () => void customersQuery.refetch(),
  };
}
